import { motion } from 'framer-motion';
import { Award, Target, Users, TrendingUp, MapPin, Zap, Building2, FileCheck, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

const AboutPage = () => {
  const [showFactorTooltip, setShowFactorTooltip] = useState(false);
  
  const values = [
    {
      icon: Users,
      title: 'Democratising ESG',
      description: 'Breaking down the "big end of town" barrier—professional carbon reporting shouldn\'t require enterprise budgets'
    },
    {
      icon: Target,
      title: 'Results-Driven',
      description: 'Practical tools that actually help you win tenders and secure contracts across Australia'
    },
    {
      icon: MapPin,
      title: 'State-Aware Intelligence',
      description: 'Our engine automatically applies the correct 2026 NGA emission factors for your specific State or Territory'
    },
    {
      icon: FileCheck,
      title: 'Audit-Ready Output',
      description: 'Professional reports aligned with AASB S2 standards that procurement officers trust'
    }
  ];

  const stateFactors = [
    { state: 'QLD', name: 'Queensland', factor: 0.67, color: '#E76F51' },
    { state: 'NSW', name: 'New South Wales', factor: 0.64, color: '#E9C46A' },
    { state: 'VIC', name: 'Victoria', factor: 0.82, color: '#2A9D8F' },
    { state: 'SA', name: 'South Australia', factor: 0.25, color: '#74A57F' },
    { state: 'WA', name: 'Western Australia', factor: 0.51, color: '#264653' },
    { state: 'TAS', name: 'Tasmania', factor: 0.15, color: '#003B5C' },
    { state: 'NT', name: 'Northern Territory', factor: 0.54, color: '#F4A261' },
    { state: 'ACT', name: 'Australian Capital Territory', factor: 0.64, color: '#94A3B8' }
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0] py-20 md:py-32" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-block mb-4">
              <span className="text-sm font-medium tracking-wide uppercase text-[#74A57F] bg-[#74A57F]/10 px-4 py-2 rounded-full">
                Our Story
              </span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <span className="text-[#003B5C]">Born in the Sunny Coast.</span>
              <br />
              <span className="text-[#74A57F]">Built for Australia.</span>
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              A climate-fintech platform helping Australian SMEs navigate the new world of carbon reporting
            </p>
          </motion.div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-20 bg-white" data-testid="origin-story">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#003B5C] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The Story
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-slate-700 leading-relaxed mb-6">
                Lucent Sky ESG started with a coffee and a challenge in the Sunshine Coast hinterland. We saw local businesses—the 
                backbone of our community—struggling to keep up with the tidal wave of new sustainability rules, like the 
                <strong> Queensland Procurement Policy 2026</strong>.
              </p>
              
              <p className="text-slate-700 leading-relaxed mb-6">
                We realised that while our roots are in the Sunny Coast, <strong>the problem is national</strong>. From the workshops 
                of Perth to the offices of Sydney, every Australian SME is now being asked to prove their "Green Status" to win 
                tenders and secure their future.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* National Reach Section with NGA Factors Visual */}
      <section className="py-16 bg-[#F8F9FA]" data-testid="national-reach">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                National Reach, Local Logic
              </h2>
              <div className="relative">
                <button 
                  className="flex items-center text-sm text-[#003B5C] hover:text-[#74A57F] transition-colors"
                  onMouseEnter={() => setShowFactorTooltip(true)}
                  onMouseLeave={() => setShowFactorTooltip(false)}
                  data-testid="nga-info-button"
                >
                  <Info className="w-5 h-5 mr-1" />
                  How NGA Factors Work
                </button>
                {showFactorTooltip && (
                  <div className="absolute right-0 top-8 w-80 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-50" data-testid="nga-tooltip">
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>National Greenhouse Accounts (NGA) Factors</strong> are the official emission factors 
                      published by the Australian Government for carbon reporting.
                    </p>
                    <p className="text-sm text-slate-600">
                      Each state has different electricity grid compositions (coal, gas, renewables), 
                      resulting in different kg CO₂-e per kilowatt-hour.
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <p className="text-lg text-slate-700 mb-8">
              We didn't just build a calculator; we built a <strong>State-Aware Engine</strong>. Lucent Sky ESG automatically 
              adjusts its maths based on where you are. Because a kilowatt-hour in the Sunny Coast isn't the same as one 
              in Hobart, our system applies the exact <strong>2026 National Greenhouse Accounts factors</strong> for your 
              specific State or Territory.
            </p>

            {/* State Factors Visual Grid */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <h3 className="text-lg font-semibold text-[#003B5C] mb-4 flex items-center">
                <Zap className="w-5 h-5 mr-2 text-[#E9C46A]" />
                Electricity Emission Factors by State (2026 NGA)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stateFactors.map((s) => (
                  <div 
                    key={s.state} 
                    className="bg-slate-50 rounded-xl p-4 text-center hover:shadow-md transition-shadow"
                    data-testid={`state-factor-${s.state}`}
                  >
                    <div 
                      className="w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold text-sm"
                      style={{ backgroundColor: s.color }}
                    >
                      {s.state}
                    </div>
                    <div className="text-2xl font-bold text-[#003B5C]">{s.factor}</div>
                    <div className="text-xs text-slate-500">kg CO₂-e/kWh</div>
                    <div className="text-xs text-slate-400 mt-1">{s.name}</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-4 text-center">
                Lower factors indicate a cleaner electricity grid. Tasmania (0.15) leads with hydropower, while Victoria (0.82) has higher coal dependence.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white" data-testid="mission-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#003B5C] to-[#005580] rounded-2xl p-8 md:p-12 shadow-xl"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Our Mission
            </h2>
            <p className="text-xl text-white/90 leading-relaxed mb-6">
              To give every Australian SME their <strong>"Green Passport"</strong>.
            </p>
            <p className="text-lg text-white/80 leading-relaxed">
              We take the messy data from your utility bills and turn it into professional, audit-grade reports that meet 
              <strong> AASB S2 standards</strong>. We handle the maths and the compliance, so you can get back to business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Now Section */}
      <section className="py-16 bg-[#F8F9FA]" data-testid="why-now">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-[#E9C46A] rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#003B5C]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Why Now?
              </h2>
            </div>
            
            <p className="text-lg text-slate-700 mb-8">
              Three national pressures are converging on Australian SMEs—and preparation is no longer optional:
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-[#E76F51]">
                <h3 className="font-semibold text-[#003B5C] mb-2">Scope 3 Supply Chain</h3>
                <p className="text-sm text-slate-600">
                  Corporate buyers now need emissions data from their SME suppliers. If you can't provide it, you're out of the tender.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-[#E9C46A]">
                <h3 className="font-semibold text-[#003B5C] mb-2">Bank & Insurance Mandates</h3>
                <p className="text-sm text-slate-600">
                  Lenders and insurers are increasingly requiring ESG disclosures. Your "green status" affects your financing terms.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-[#74A57F]">
                <h3 className="font-semibold text-[#003B5C] mb-2">Legislative Changes</h3>
                <p className="text-sm text-slate-600">
                  AASB S2 climate disclosures and policies like the QPP 2026 are making compliance mandatory—not just "nice to have".
                </p>
              </div>
            </div>

            <div className="mt-8 bg-white rounded-xl p-6 border border-slate-200">
              <p className="text-slate-700">
                <strong className="text-[#003B5C]">Plus, the 2032 Brisbane Olympics</strong> are driving unprecedented infrastructure 
                investment across Queensland. Suppliers without documented sustainability practices will miss out on billions in 
                contract opportunities.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section - What Drives Us */}
      <section className="py-20 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003B5C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What Drives Us
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Democratising ESG for Australian SMEs—because professional carbon reporting shouldn't be reserved for the big end of town
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center p-6"
                  data-testid={`value-card-${index}`}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#003B5C] to-[#74A57F] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#003B5C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {value.title}
                  </h3>
                  <p className="text-slate-600">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#003B5C] to-[#74A57F]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Get Your Green Passport?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join Australian businesses taking control of their sustainability reporting
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/signup">
              <Button 
                className="bg-white text-[#003B5C] hover:bg-slate-50 rounded-full px-10 py-6 text-lg font-medium shadow-xl transition-all" 
                data-testid="cta-free-trial"
              >
                Start Free Demo
              </Button>
            </a>
            <a href="/contact">
              <Button 
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full px-10 py-6 text-lg font-medium transition-all" 
                data-testid="cta-contact"
              >
                Get in Touch
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
